import React, { useEffect, useState, useMemo } from "react";
import { ArrowLeft, Search, List, Square } from "lucide-react";
import StationCardList from "./StationCardList";
import { fetchStationPerformance } from "../../api/analytics/getStationPerformance";
import { useUser } from "../../context/UserContext";
import Filter from "../../components/layout/Filter";

interface StationPerformance {
  station_id: string;
  station_name: string;
  total_sessions: number;
  consumed_kwh: number;
  total_revenue: number;
  avg_revenue_per_session: number;
  total_chargers: number;
  active_chargers: number;
}

const StationsPage: React.FC = () => {
  const { user } = useUser();

  const [stations, setStations] = useState<StationPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [metrics, setMetrics] = useState([
    { label: "Total stations", value: "0", options: true },
    { label: "Online stations", value: "0", options: true },
    { label: "Active chargers", value: "0", options: true },
    { label: "Total revenue", value: "₹0", options: true },
  ]);

  const formatLocalDate = (date: Date, endOfDay = false) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const time = endOfDay ? "23:59:59" : "00:00:00";
    return `${year}-${month}-${day}T${time}`;
  };

  const loadStations = async (startDate: Date, endDate: Date) => {
    if (!user?.org_id) {
      console.warn("Organization ID missing — skipping station fetch.");
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const start = formatLocalDate(startDate, false);
      const end = formatLocalDate(endDate, true);

      const data = await fetchStationPerformance(user.org_id, start, end);

      setStations(data);

      const totalStations = data.length;
      const onlineStations = data.filter((stn) => stn.active_chargers > 0).length;
      const totalActiveChargers = data.reduce(
        (acc, stn) => acc + (stn.active_chargers || 0),
        0
      );
      const totalRevenue = data.reduce(
        (acc, stn) => acc + (stn.total_revenue || 0),
        0
      );

      setMetrics([
        { label: "Total stations", value: String(totalStations), options: true },
        { label: "Online stations", value: String(onlineStations), options: true },
        {
          label: "Active chargers",
          value: String(totalActiveChargers),
          options: true,
        },
        {
          label: "Total revenue",
          value: `₹${totalRevenue.toLocaleString()}`,
          options: true,
        },
      ]);
    } catch (err) {
      console.error("Error loading stations:", err);
      setError("Failed to load station performance data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && dateRange.startDate && dateRange.endDate) {
      loadStations(dateRange.startDate, dateRange.endDate);
    }
  }, [user]);

  const handleApplyFilter = (start: Date, end: Date) => {
    setDateRange({ startDate: start, endDate: end });
    loadStations(start, end); 
  };

  const filteredStations = useMemo(() => {
    return stations.filter((station) => {
      const matchesSearch = station.station_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "online"
          ? station.active_chargers > 0
          : station.active_chargers === 0;

      return matchesSearch && matchesStatus;
    });
  }, [stations, searchTerm, statusFilter]);

  if (loading)
    return (
      <div className="loader-container">
        <div className="loader"></div>
      </div>
    );

  return (
    <div className="stations-page-layout">
      {/* <div className="stations-global-header">
        <span className="breadcrumb-link">
          <a href="/dashboard">
            <ArrowLeft size={16} />{" "}
          </a>
          ChargingAdda's Charging Management Software
        </span>
        <div className="tooltip-wrapper">
          <button className="add-station-btn disable-btn" disabled>
            Add station
          </button>
          <span className="tooltip-text">Coming Soon</span>
        </div>
      </div> */}

      <div className="stations-top-controls">
        <div className="search-bar-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search stations by name or city..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <Filter
            defaultStart={dateRange.startDate}
            defaultEnd={dateRange.endDate}
            onApply={handleApplyFilter} 
          />
          <select
            className="status-dropdown"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All status</option>
            <option value="online">Online</option>
          </select>

          <button className="view-toggle-btn active">
            <List size={18} />
          </button>
          <button className="view-toggle-btn">
            <Square size={18} />
          </button>
        </div>
      </div>

      <div className="stations-metrics-grid">
        {metrics.map((metric, index) => (
          <div key={index} className="card station-metric-card">
            <div className="metric-header">
              <h3>{metric.label}</h3>
              {metric.options && <span className="more-options">...</span>}
            </div>
            <p className="metric-value">{metric.value}</p>
          </div>
        ))}
      </div>

      {filteredStations.length > 0 ? (
        <StationCardList stations={filteredStations} />
      ) : (
        <div className="no-results">No stations found.</div>
      )}
    </div>
  );
};

export default StationsPage;
